import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  useTheme,
  Typography,
  Stack,
  Divider,
} from "@mui/material";
import Container from "@mui/material/Container";
import moment from "moment";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router";
import { API_URLS, ResDataType } from "src/api/apiUrls";
import useApiServices from "src/api/useApiServices";
import Chart, { useChart } from "src/components/chart";
import AppLoadingAndErrorWrapper from "src/components/common/AppLoadingAndErrorWrapper";
import CustomBreadcrumbs from "src/components/custom-breadcrumbs";
import Header from "src/components/Header";
import { useSettingsContext } from "src/components/settings";
import StatisticCard from "src/components/StatisticCard";
import { paths } from "src/routes/paths";
import ProgramStatusStatistics from "src/sections/analytics/ProgramStatusStatistics";
import ChartColumnSingle from "src/sections/chart-view/ChartColumnSingle";

export default function ProgramAnalytics () {
  const { programId } = useParams();
  const settings = useSettingsContext();
  const { useGetListApi } = useApiServices();

  const { data, isLoading, error } = useGetListApi<
    ResDataType["programAnalytics"]
  >({
    url: API_URLS.PROGRAM_ANALYTICS,
    parameters: {
      programId: programId!,
    },
  });

  const totalUserCount = data?.usersIslamicType.reduce(
    (acc, user) => acc + user.count,
    0
  );

  const totalProgramFrqCount = data?.usersIslamicType.reduce(
    (acc, prg) => acc + prg.count,
    0
  );

  const theme = useTheme();

  const colors = [
    [theme.palette.primary.light, theme.palette.primary.main],
    [theme.palette.warning.light, theme.palette.warning.main],
  ];

  const CHART_SIZE = { width: 106, height: 106 };

  const chartOptionsCheckIn = useChart({
    fill: {
      type: "gradient",
      gradient: {
        colorStops: ([
          { offset: 0, color: colors[0][0] },
          { offset: 100, color: colors[0][1] },
        ] as any),
      },
    },
    chart: {
      sparkline: {
        enabled: true,
      },
    },
    grid: {
      padding: {
        top: -9,
        bottom: -9,
      },
    },
    legend: {
      show: false,
    },
    plotOptions: {
      radialBar: {
        hollow: { size: "64%" },
        track: { margin: 0 },
        dataLabels: {
          name: { show: false },
          value: {
            offsetY: 6,
            fontSize: theme.typography.subtitle2.fontSize as string,
          },
        },
      },
    },
  });

  return (
    <>
      <Helmet>
        <title>Home</title>
      </Helmet>
      <Container maxWidth={settings.themeStretch ? false : "xl"}>
        <AppLoadingAndErrorWrapper isLoading={isLoading} errorMessage={error}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Header header={`Today, ${moment().format("YYYY-MM-DD")}`} />
            </Grid>
            {data?.programMessages?.map((e, index) => (
              <Grid item xs={12} md={12 / data.programMessages.length}>
                <StatisticCard
                  cardCount={e.count}
                  cardTitle={e.label}
                  index={index}
                />
              </Grid>
            ))}

            <Grid item xs={12} md={6} display='flex'>
              <Card sx={{ width: "100%" }}>
                <CardHeader title='Total subscription' />
                <CardContent>
                  <ChartColumnSingle
                    series={[
                      {
                        name: "",
                        data: data!?.totalSubscriptionStatistics.map(
                          e => e.count
                        ),
                      },
                    ]}
                    xaxis={data!?.totalSubscriptionStatistics.map(e => e.month)}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} display='flex'>
              <ProgramStatusStatistics
                sx={{ width: "100%" }}
                title='Program status'
                data={data!?.programStatus.map(e => ({
                  status: e.label!,
                  value: e.count!,
                }))}
              />
            </Grid>

            <Grid item xs={12} display='flex'>
              <Card sx={{ width: "100%" }}>
                <CardContent
                  sx={{
                    width: "100%",
                  }}
                >
                  <Stack spacing={4}>
                    <Stack>
                      <Typography
                        variant='cardHeader'
                        sx={{ opacity: 0.72, width: "fit-content" }}
                      >
                        User Type
                      </Typography>
                      <Divider />
                    </Stack>
                    <Stack
                      flexDirection={{ md: "row", xs: "column" }}
                      alignItems='center'
                      spacing={2}
                      width='100%'
                      justifyContent='space-evenly'
                    >
                      {data?.usersIslamicType.map(e => (
                        <Stack
                          flexDirection='row'
                          alignItems='center'
                          width={{ xs: "100%", md: "auto" }}
                          justifyContent={{ xs: "space-evenly" }}
                        >
                          <Chart
                            type='radialBar'
                            series={[
                              +((e.count / totalUserCount!) * 100).toFixed(1) || 0,
                            ]}
                            options={chartOptionsCheckIn}
                            {...CHART_SIZE}
                          />
                          <div>
                            <Typography variant='body2' sx={{ opacity: 0.72 }}>
                              {e.label}
                            </Typography>
                          </div>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} display='flex'>
              <Card sx={{ width: "100%" }}>
                <CardContent
                  sx={{
                    width: "100%",
                  }}
                >
                  <Stack spacing={4}>
                    <Stack>
                      <Typography
                        variant='cardHeader'
                        sx={{ opacity: 0.72, width: "fit-content" }}
                      >
                        Programs Frequency
                      </Typography>
                      <Divider />
                    </Stack>
                    <Stack
                      flexDirection={{ md: "row", xs: "column" }}
                      alignItems='center'
                      spacing={2}
                      width='100%'
                      justifyContent='space-evenly'
                    >
                      {data?.usersProgramFrequency.map(e => (
                        <Stack
                          flexDirection='row'
                          alignItems='center'
                          width={{ xs: "100%", md: "auto" }}
                          justifyContent={{ xs: "space-evenly" }}
                        >
                          <Chart
                            type='radialBar'
                            series={[
                              +(
                                (e.count / totalProgramFrqCount!) *
                                100
                              ).toFixed(1) || 0
                            ]}
                            options={chartOptionsCheckIn}
                            {...CHART_SIZE}
                          />
                          <div>
                            <Typography variant='body2' sx={{ opacity: 0.72 }}>
                              {e.label}
                            </Typography>
                          </div>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </AppLoadingAndErrorWrapper>
      </Container>
    </>
  );
}
